import { Injectable } from "@angular/core"
import { ApiService, CreatorsAPIResponse } from "./api.service"
import {
    ContentDeleteParameters,
    ContentMoveParameters,
    DownloadTrackParameters,
    Folder,
    GenerateSongParameters,
    GenerateLyricsParameters,
    GetContentResponse,
    LikeContentParameters,
    LyricsChatStreamEvent,
    LyricsMode,
    RenameContentParameters,
    SongGenerationStreamEvent,
    VocalGender,
} from "@common-lib/interfaces/api/content.api"
import { TokenService } from "./token.service"

@Injectable()
export class TracksHttpService {
    constructor(private http: ApiService, private tokenService: TokenService) {}

    private readonly getContentURL = "/content/get"
    private readonly createFolderURL = "/content/createFolder"
    private readonly generateTrackURL = "/content/generate"
    private readonly generateLyricsURL = "/content/generateLyrics"
    private readonly generateSongURL = "/content/generateSong"

    public renameContent(params: RenameContentParameters) {
        return this.http.fetchRequest({
            url: "/content/rename/v2",
            parameters: params,
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    public getDownloadURL(
        params: DownloadTrackParameters
    ): Promise<{ link: string; newQuota: number }> {
        return this.http.fetchRequest({
            url: "/content/downloadTrack",
            parameters: params,
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    public createFolder(parentID: string): Promise<Folder> {
        console.info({ parentID })
        return this.http.fetchRequest({
            url: this.createFolderURL,
            parameters: {
                parentID,
            },
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    private async convertFileArrayBufferToBase64(file: File): Promise<string> {
        const arrayBuffer = await file.arrayBuffer()

        return new Promise((resolve, reject) => {
            const blob = new Blob([arrayBuffer]) // Create a Blob from the ArrayBuffer
            const reader = new FileReader()

            reader.onloadend = () => {
                const base64String = reader.result?.toString().split(",")[1] // Extract Base64 string
                if (base64String) {
                    resolve(base64String)
                } else {
                    reject("Failed to convert ArrayBuffer to Base64")
                }
            }

            reader.onerror = () => {
                reject("Error reading ArrayBuffer")
            }

            reader.readAsDataURL(blob) // Read the Blob as a Data URL
        })
    }

    public async generateTrack(
        parentID: string,
        prompt: string,
        audioInfluence: File | undefined,
        duration: number,
        lyrics?: string,
        lyricsMode?: LyricsMode,
        vocalGender?: VocalGender
    ): Promise<any> {
        const audioInfluenceString = audioInfluence
            ? await this.convertFileArrayBufferToBase64(audioInfluence)
            : undefined

        console.info({ parentID, prompt })

        return this.http.fetchRequest({
            url: this.generateTrackURL,
            parameters: {
                audioInfluence: audioInfluenceString,
                parentID,
                prompt,
                duration,
                lyrics,
                lyricsMode,
                vocalGender,
            },
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    public generateLyrics(
        parameters: GenerateLyricsParameters,
        controller: AbortController,
        onMessage: (message: LyricsChatStreamEvent) => void
    ): Promise<void> {
        return this.http.fetchStreamRequest<LyricsChatStreamEvent>({
            url: this.generateLyricsURL,
            parameters: parameters as unknown as Record<string, unknown>,
            token: this.tokenService.getToken(),
            controller,
            onMessage,
        })
    }

    public generateSong(
        parameters: GenerateSongParameters,
        onMessage: (message: SongGenerationStreamEvent) => void
    ): { completion: Promise<void>; cancel: () => void } {
        const controller = new AbortController()

        return {
            completion: this.http.fetchStreamRequest<SongGenerationStreamEvent>(
                {
                    url: this.generateSongURL,
                    parameters: parameters as unknown as Record<
                        string,
                        unknown
                    >,
                    token: this.tokenService.getToken(),
                    controller,
                    onMessage,
                }
            ),
            cancel: () => controller.abort(),
        }
    }

    public likeTrack(params: LikeContentParameters): Promise<any> {
        return this.http.fetchRequest({
            url: "/content/like",
            parameters: params,
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    public deleteContent(params: ContentDeleteParameters): Promise<any> {
        return this.http.fetchRequest({
            url: "/content/delete",
            parameters: params,
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    public moveContent(
        params: ContentMoveParameters
    ): Promise<CreatorsAPIResponse> {
        return this.http.fetchRequest({
            url: "/content/move",
            parameters: params,
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }

    public getFolderContent(parentID: string): Promise<GetContentResponse> {
        return this.http.fetchRequest({
            url: this.getContentURL,
            parameters: {
                selectedFolderID: parentID,
            },
            urlType: "primary",
            type: "POST",
            controller: undefined,
            token: this.tokenService.getToken(),
        }) as any
    }
}
